import { Component, ChangeDetectionStrategy } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { TranslateService } from "@ngx-translate/core";

import { BaseComponent, EventsService, UserService } from "@mypxplat/xplat/core";
import { BetaService, WebCommunityService, AppService } from "@mypxplat/xplat/web/core";
import { filter, take } from "rxjs";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-notification-preferences",
  templateUrl: "notification-preferences.component.html",
  standalone: false,
})
export class NotificationPreferencesComponent extends BaseComponent {
  public clients: Array<any> = [
    {
      title: "Email",
      id: "email",
      value: true,
    },
    {
      title: "Push Notifications in the MyPreSonus App (iOS/Android)",
      id: "mobile",
      value: true,
    },
    {
      title: "Notifications in notification center, Studio One and other desktop apps.",
      id: "desktop",
      value: true,
    },
  ];
  public showBetaTopics = false;
  public notificationTopics: Array<any> = [
    {
      topic: "collaboration",
      title: "Collaboration",
      description: "These notifications are delivered when workspaces you own or collaborate on are updated.",
      clients: {},
    },
    {
      topic: "community",
      title: "Community",
      description: "These notifications are delivered when other community members interact with you.",
      clients: {},
    },
  ];

  public betaCommunityNotificationPreferences: any = {};

  constructor(
    userService: UserService,
    public communityService: WebCommunityService,
    public betaService: BetaService,
    public translate: TranslateService,
    public eventsService: EventsService,
    public appService: AppService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.loadEventReminders();
    this.communityService.getFirebaseProfile().then((profile: any) => {
      // let isBetaManager = this.user.groups.some((group: any) => group.name === "beta_manager");
      // if (isBetaManager) {
      if (profile.betaCommunityNotificationPreferences) this.betaCommunityNotificationPreferences = profile.betaCommunityNotificationPreferences;
      this.betaService.gotBetas$
        .pipe(
          filter((item) => !!item),
          take(1)
        )
        .subscribe((result) => {
          if (this.betaService.enrolledBetas && this.betaService.enrolledBetas.length > 0) {
            this.showBetaTopics = true;
            const betaCommunityTopic = {
              topic: "beta_community",
              title: this.translate.instant("account.beta_communities"),
              description: this.translate.instant("account.beta_communities_description"),
              clients: {},
            };
            if (profile.notificationPreferences?.beta_community) {
              betaCommunityTopic.clients = profile.notificationPreferences.beta_community;
            } else {
              this.clients.forEach((client) => {
                betaCommunityTopic.clients[client.id] = true;
              });
            }
            this.notificationTopics.push(betaCommunityTopic);
            this.betaService.enrolledBetas.forEach((id) => {
              if (!this.betaCommunityNotificationPreferences[id]) {
                this.betaCommunityNotificationPreferences[id] = {
                  new: false,
                  relevant_replies: true,
                  all_replies: false,
                };
              }
            });
          }
        });
      // }

      if (profile.notificationPreferences) {
        for (var i in profile.notificationPreferences) {
          this.notificationTopics.forEach((topic) => {
            if (topic.topic == i) {
              topic.clients = profile.notificationPreferences[i];
            }
          });
          this.notificationTopics.forEach((item) => {
            if (!profile.notificationPreferences[item.topic]) {
              this.clients.forEach((client) => {
                item.clients[client.id] = true;
              });
            }
          });
        }
      } else {
        this.notificationTopics.forEach((item) => {
          this.clients.forEach((client) => {
            item.clients[client.id] = true;
          });
        });
      }
    });
  }

  get betaCommunityChannelsAllOff(): boolean {
    const topic = this.notificationTopics.find((t) => t.topic === "beta_community");
    if (!topic) return false;
    return !topic.clients.email && !topic.clients.mobile && !topic.clients.desktop;
  }

  // Event reminders the user is subscribed to (DENG-98)
  public eventReminders: any[] = [];
  public loadingReminders: boolean = false;

  loadEventReminders() {
    this.loadingReminders = true;
    this.eventsService.getMyEventReminders().subscribe({
      next: (reminders) => {
        this.eventReminders = (reminders || []).map((r) => ({ ...r, whenLabel: this.formatReminderWhen(r) }));
        this.loadingReminders = false;
      },
      error: () => {
        this.loadingReminders = false;
      },
    });
  }

  removeEventReminder(reminder: any) {
    this.eventsService.deleteEventReminder(reminder.slug).subscribe({
      next: () => {
        this.eventReminders = this.eventReminders.filter((r) => r.slug !== reminder.slug);
      },
      error: () => {
        this.appService.alert("Sorry, we couldn't remove that reminder. Please try again.");
      },
    });
  }

  private formatReminderWhen(reminder: any): string {
    const s = reminder?.start_datetime;
    if (!s) return "";
    const [datePart, timePart] = s.split("T");
    const [y, mo, d] = (datePart || "").split("-").map(Number);
    const [h, mi] = (timePart || "00:00").split(":").map(Number);
    const dt = new Date(y, (mo || 1) - 1, d || 1, h || 0, mi || 0);
    const dateStr = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    const timeStr = dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${dateStr} at ${timeStr}`;
  }

  onBetaCommunityChannelToggle(topic: any, clientId: string) {
    topic.clients[clientId] = !topic.clients[clientId];
    if (this.betaCommunityChannelsAllOff) {
      this.betaService.enrolledBetas.forEach((id) => {
        this.betaCommunityNotificationPreferences[id] = {
          new: false,
          relevant_replies: false,
          all_replies: false,
        };
      });
    }
  }

  save() {
    let data = {};
    this.notificationTopics.forEach((item) => {
      data[item.topic] = item.clients;
    });
    let notificationPrefs: any = {
      notificationPreferences: data,
    };
    if (this.showBetaTopics) {
      notificationPrefs.betaCommunityNotificationPreferences = this.betaCommunityNotificationPreferences;
    }
    this.communityService.updateProfile(notificationPrefs).then(() => {
      alert("Preferences saved.");
    });
  }
}
